// @flow
import React, {useEffect, useState} from "react";
import {useHistory, Link} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {
    Col,
    Skeleton,
    Row,
    Table,
    Descriptions,
    Tag,
    Select,
    Empty,
    Upload,
    message,
    Space
} from "antd";
import {
    EditOutlined,
    ShopOutlined,
    CloseOutlined,
    CheckOutlined,
    PlusOutlined,
    UploadOutlined,
    DownloadOutlined,
    SearchOutlined
} from "@ant-design/icons";
import moment from "moment";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {companyDetailPropType, companyType, subCompanyType} from "./companyDetailPropTypes";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {CityContainer, Img, MarkerImg, OrderMergeContainer} from "./companyDetailStyles";
import config from "../../../_core/config";

import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import {courierTypes, workingTypes, smartLockerProviders} from "../../../constants/constants";
import placeholder from "../../../public/images/company-placeholder.jpg";
import {useTranslation} from "react-i18next";
import {ApiIntegrationMailPopconfirm} from "./Components/ApiIntegrationPopconfirm/ApiIntegrationMailPopconfirm";
import {
    CloseIcon,
    SearchInput
} from "../../_defaultComponents/FilterComponents/FilterComponentStyles";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import OnboardingStatusPopConfirm from "./Components/OnboardingStatusPopConfirm/OnboardingStatusPopConfirm";
import {ChangeSubcompanyOnboardingStatusInput} from "../../_defaultComponents/ChangeSubcompanyOnboardingStatusInput/ChangeSubcompanyOnboardingStatusInput";
import {ChangeSubcompanyStatusModal} from "./Components/ChangeSubcompanyStatusModal/ChangeSubcompanyStatusModal";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const CompanyDetail = ({companyId}: companyDetailPropType) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation([
        "companyDetail",
        "courierTypes",
        "companyUserSelect",
        "newCompany"
    ]);
    const [activity, setActivity] = useState("all");
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [companyRecall, setCompanyRecall] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [searchCityText, setSearchCityText] = useState("all");
    const [cityText, setCityText] = useState("");
    const [isTestUser, setIsTestUser] = useState(false);
    const [
        subCompanyUpdateStatusModalVisibility,
        setSubCompanyUpdateStatusModalVisibility
    ] = useState(false);
    const [selectedSubCompany, setSelectedSubCompany] = useState(undefined);

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: true
        }),
        dependencies: [companyRecall, activity]
    });

    const [
        subCompanyLoading,
        subCompanyData,
        setSubCompanyData,
        setSubCompanyLoading,
        refetch
    ] = useFetch({
        defaultDataValue: {},
        fetchFn: companyData?.data?._id
            ? stores.companyStore.getSubCompanyList.bind(null, {
                  companyId: companyData.data._id,
                  limit: 10,
                  skip: (currentPage - 1) * 10,
                  activity,
                  searchText: searchText.toLocaleLowerCase("tr-TR"),
                  city: searchCityText.toLocaleLowerCase("tr-TR")
              })
            : () => [],
        dependencies: [
            currentPage,
            activity,
            companyData,
            subCompanyRecall,
            searchText,
            searchCityText
        ]
    });

    const handleSubCompanyUpdateStatus = (subCompany: subCompanyType) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("companyDetail.modal.subcompanyUpdateStatus.content", {
                ns: "companyDetail",
                disabledStatus: subCompany.disabled
                    ? t("other.activeLowercase", {ns: "common"})
                    : t("other.inactiveLowercase", {ns: "common"})
            }),
            onOk: async () => {
                setSubCompanyLoading(true);
                const updatedCompany = await stores.companyStore.updateSubCompanyStatus({
                    subCompany: {
                        ...subCompany,
                        disabled: !subCompany.disabled
                    }
                });
                if (updatedCompany) {
                    setSubCompanyRecall(!subCompanyRecall);
                }
                setSubCompanyLoading(false);
            }
        });
    };

    const handleSubCompanyOnboardingStatusUpdate = async row => {
        try {
            const newStatus = row.onboardingStatus === "candidate" ? "approved" : "candidate";
            const updateData = {
                _id: row._id,
                location: row.location,
                companyId: row.companyId,
                companyName: row.companyName,
                disabled: row.disabled,
                onboardingStatus: newStatus
            };
            await stores.companyStore.updateSubCompany(updateData);
            refetch();
        } catch (error) {
            console.error("Alt şirket onboarding durumu güncellenirken hata oluştu:", error);
        }
    };

    useEffect(() => {
        if (companyData.status && companyData.status !== 200) {
            stores.appStore.openErrorModal({
                content: t("other.companyNotFound", {ns: "common"}),
                onOk: () => {
                    history.push("/companies");
                }
            });
        }
        if (companyData.status && companyData.data.deliveryList) {
            selectedSubCompanyForUpdateStatus(companyData.data.deliveryList[0]);
            const couriers = companyData.data.deliveryList
                .filter(delivery => delivery.deliveryState !== "unassigned")
                .map(delivery => delivery.assignedCourier);
            setCourierList(couriers);
        }
    }, [companyData]);

    const fileUploadProps = {
        name: "file",
        multiple: false,
        action: `${config.getBasePublicUrl()}api/panel/v1/company/subcompany/import/${companyId}`,
        headers: {
            Authorization: "Basic " + stores.meStore.sessionToken
        },

        showUploadList: false,

        onChange: async info => {
            const {status} = info.file;
            if (status === "uploading") {
                setSubCompanyLoading(true);
            }
            if (status === "done") {
                stores.appStore.openSuccessModal({
                    title: t("companyDetail.modal.fileUpload.success", {ns: "companyDetail"}),
                    content: info.file.response.message
                });
                setSubCompanyRecall(!subCompanyRecall);
                setSubCompanyLoading(false);
            } else if (status === "error") {
                message.error(
                    t("companyDetail.modal.fileUpload.error", {
                        ns: "companyDetail",
                        filename: info.file.name,
                        responseMessage: info.file.response.message
                    })
                );
                setSubCompanyLoading(false);
            }
        }
    };

    const selectedSubCompanyForUpdateStatus = subCompany => {
        setSelectedSubCompany(subCompany);
        setSubCompanyUpdateStatusModalVisibility(true);
    };

    return useObserver(() =>
        !companyData.data ? (
            <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
        ) : (
            <>
                <Row gutter={[0, 20]} data-testid="companyDetailScreen">
                    <Col span={24}>
                        <Descriptions
                            bordered
                            column={2}
                            title={
                                <>
                                    <Row type="flex" justify="space-between">
                                        <span data-testid="companyName">
                                            {companyData.data.companyName}
                                        </span>
                                        {stores.meStore.checkRole("updateCompany") && (
                                            <ConditionalTooltip
                                                text={t(
                                                    "companyDetail.disabledUpdateCompanyTooltip",
                                                    {ns: "companyDetail"}
                                                )}
                                                condition={companyData.data.disabled}
                                                child={
                                                    <CustomButton
                                                        type="primary"
                                                        data-testid="companyEditButton"
                                                        href={
                                                            "/companies/edit/" +
                                                            companyData.data._id
                                                        }
                                                        onClick={event => {
                                                            event.preventDefault();
                                                            history.push(
                                                                "/companies/edit/" +
                                                                    companyData.data._id
                                                            );
                                                        }}
                                                        disabled={companyData.data.disabled}
                                                        icon={<EditOutlined />}
                                                    />
                                                }
                                            />
                                        )}
                                    </Row>
                                    <Row>
                                        <Img
                                            onError={({currentTarget}) => {
                                                currentTarget.onerror = null; // prevents looping
                                                currentTarget.src = placeholder;
                                            }}
                                            src={
                                                `${config.awsUrl}${companyData?.data?.photoId}` ||
                                                placeholder
                                            }
                                            data-testid="companyImage"
                                        />
                                    </Row>
                                </>
                            }
                        >
                            <Descriptions.Item
                                span={1}
                                label={t("fields.companyActivity", {ns: "common"})}
                            >
                                <span data-testid="companyActivity">
                                    {companyData.data.disabled
                                        ? t("other.inactive", {ns: "common"})
                                        : t("other.active", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={1}
                                label={t("fields.testUser", {ns: "common"})}
                            >
                                <span data-testid="testUser">
                                    {companyData.data.isTestUser
                                        ? t("other.yes", {ns: "common"})
                                        : t("other.no", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={1}
                                label={t("fields.companyCode", {ns: "common"})}
                            >
                                <span data-testid="companyCode">
                                    {companyData.data.companyCode || "-"}
                                </span>
                            </Descriptions.Item>

                            <Descriptions.Item label={t("fields.createdDate", {ns: "common"})}>
                                <span data-testid="createdAt">
                                    {moment(companyData.data.createdAt).format("ll")}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.city", {ns: "common"})}>
                                <span data-testid="city">
                                    {companyData.data.city?.capitalize()}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.county", {ns: "common"})}>
                                <span data-testid="county">
                                    {companyData.data.county?.capitalize()}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item span={2} label={t("fields.address", {ns: "common"})}>
                                <span data-testid=""></span>
                                {companyData.data.address}
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={2}
                                label={t("fields.taxOffice", {ns: "common"})}
                            >
                                <span data-testid="registeredTaxOffice">
                                    {companyData.data.registeredTaxOffice}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={2}
                                label={t("fields.taxNumber", {ns: "common"})}
                            >
                                <span data-testid="taxNumber">{companyData.data.taxNumber}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.bankName", {ns: "common"})}>
                                <span data-testid="bankName">{companyData.data.bankName}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="IBAN">
                                <span data-testid="iban">{companyData.data.iban}</span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={2}
                                label={t("fields.billInformation", {
                                    ns: "common"
                                })}
                            >
                                <span data-testid="billInformation">
                                    {companyData.data.billInformation}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.courierTypes", {ns: "common"})}>
                                <span data-testid="courierTypes">
                                    {companyData.data.courierTypes &&
                                        courierTypes
                                            .filter(type =>
                                                companyData.data.courierTypes.includes(type.value)
                                            )
                                            .map(ty => (
                                                <Tag key={ty.value}>
                                                    {t(ty.translation_key, {
                                                        ns: "courierTypes"
                                                    })}
                                                </Tag>
                                            ))}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.companyWorkingTypes", {ns: "common"})}
                            >
                                <span data-testid="companyWorkingTypes">
                                    {companyData.data.workingTypes &&
                                        workingTypes
                                            .filter(type =>
                                                companyData.data.workingTypes.includes(type.value)
                                            )
                                            .map(ty => (
                                                <Tag key={ty.value}>
                                                    {t(ty.translation_key, {
                                                        ns: "companyWorkingTypes"
                                                    })}
                                                </Tag>
                                            ))}
                                </span>
                            </Descriptions.Item>
                            {companyData.data.smartLockerProvider && (
                                <Descriptions.Item
                                    label={t("fields.smartLockerProvider", {ns: "common"})}
                                >
                                    <span data-testid="smartLockerProvider">
                                        {smartLockerProviders
                                            .filter(
                                                type =>
                                                    type.value ===
                                                    companyData.data.smartLockerProvider
                                            )
                                            .map(provider => (
                                                <Tag key={provider.value}>
                                                    {provider.name || provider.value}
                                                </Tag>
                                            ))}
                                    </span>
                                </Descriptions.Item>
                            )}
                            {stores.meStore.checkRole("newRequest") && (
                                <>
                                    <Descriptions.Item
                                        span={2}
                                        label={t("fields.apiKey", {ns: "common"})}
                                    >
                                        <span data-testid="apiKey">{companyData.data.apiKey}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={2}
                                        label={t("fields.apiSecret", {ns: "common"})}
                                    >
                                        <span data-testid="apiSecret">
                                            {companyData.data.apiSecret}
                                        </span>
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        span={2}
                                        label={t("newCompany.fields.apiIntegration", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <span data-testid="isApiIntegrationActive">
                                            {Boolean(companyData.data?.isApiIntegrationActive)
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={2}
                                        label={t("newCompany.fields.preparedOrder", {
                                            ns: "newCompany"
                                        })}
                                    >
                                        <span data-testid="isOrderPreparedActive">
                                            {Boolean(companyData.data?.isOrderPreparedActive)
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={2}
                                        label={t("fields.apiDocumentation", {ns: "common"})}
                                    >
                                        <span data-testid="apiDocumentation">
                                            <ApiIntegrationMailPopconfirm
                                                companyData={companyData.data}
                                            />
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={1}
                                        label={t("fields.callbackUrl", {ns: "common"})}
                                    >
                                        <span data-testid="callbackUrl">
                                            {companyData.data.callback?.url || "-"}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item span={1} label="Token Bilgileri">
                                        <div>
                                            <div>
                                                {t("fields.callbackTokenHeader", {
                                                    ns: "common"
                                                })}
                                                :
                                                <span
                                                    data-testid="callbackTokenHeader"
                                                    style={{marginLeft: "4px"}}
                                                >
                                                    {companyData.data.callback?.tokenHeader || "-"}
                                                </span>
                                            </div>
                                            <div style={{marginTop: "4px"}}>
                                                {t("fields.callbackToken", {ns: "common"})}:
                                                <span
                                                    data-testid="callbackToken"
                                                    style={{marginLeft: "4px"}}
                                                >
                                                    {companyData.data.callback?.token || "-"}
                                                </span>
                                            </div>
                                        </div>
                                    </Descriptions.Item>
                                </>
                            )}
                            {companyData.data.markerImage && (
                                <Descriptions.Item
                                    label={t("fields.companyMarkerImage", {ns: "common"})}
                                >
                                    <MarkerImg
                                        src={companyData.data.markerImageSrc}
                                        data-testid="markerImage"
                                    />
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item
                                label={t("fields.targetDeliveryTime", {ns: "common"})}
                            >
                                <span data-testid="targetDeliveryTime">
                                    {Boolean(companyData.data?.targetDeliveryTime)
                                        ? companyData.data?.targetDeliveryTime +
                                          " " +
                                          t("units.minute.long", {ns: "common"})
                                        : "-"}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("newCompany.fields.assignmentByDeliveryPoint", {
                                    ns: "newCompany"
                                })}
                            >
                                <span data-testid="assignmentByDeliveryPoint">
                                    {Boolean(companyData.data?.isAssignmentByDeliveryPointActive)
                                        ? t("other.active", {ns: "common"})
                                        : t("other.passive", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("newCompany.fields.locationUpdateInterval", {
                                    ns: "newCompany"
                                })}
                            >
                                <span data-testid="locationUpdateInterval">
                                    {Boolean(companyData.data?.locationUpdateInterval)
                                        ? companyData.data?.locationUpdateInterval +
                                          " " +
                                          t("units.second.long", {ns: "common"})
                                        : t("other.default", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("newCompany.fields.heartbeatInterval", {
                                    ns: "newCompany"
                                })}
                            >
                                <span data-testid="heartbeatInterval">
                                    {Boolean(companyData.data?.heartbeatInterval)
                                        ? companyData.data?.heartbeatInterval +
                                          " " +
                                          t("units.second.long", {ns: "common"})
                                        : t("other.default", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("newCompany.fields.courierSearchDistance", {
                                    ns: "newCompany"
                                })}
                            >
                                <span data-testid="courierSearchDistance">
                                    {Boolean(companyData.data?.courierSearchDistance)
                                        ? companyData.data?.courierSearchDistance +
                                          " " +
                                          t("units.meter.long", {ns: "common"})
                                        : t("other.default", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("newCompany.fields.manuelDedication", {
                                    ns: "newCompany"
                                })}
                            >
                                <span data-testid="manuelDedication">
                                    {Boolean(companyData.data?.isManuelDedicationActive)
                                        ? t("other.active", {ns: "common"})
                                        : t("other.passive", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                        </Descriptions>
                        {stores.meStore.role == "admin" &&
                            companyData.data?.options?.merge?.map((mergeOptions, i) =>
                                mergeOptions.isActive ? (
                                    <OrderMergeContainer key={i} size="small" bordered>
                                        <Descriptions.Item
                                            span={3}
                                            label={t("fields.courierType", {ns: "common"})}
                                        >
                                            <span data-testid="courierType">
                                                {mergeOptions?.courierType === "motorCourier"
                                                    ? t("longForm.motorCourier", {
                                                          ns: "courierTypes"
                                                      })
                                                    : t("longForm.carCourier", {
                                                          ns: "courierTypes"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.orderMerge.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="orderMergeable">
                                                {mergeOptions?.isActive
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.clickToDoorTime.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="clickToDoorTime">
                                                {mergeOptions?.clickToDoorTime +
                                                    " " +
                                                    t("units.minute.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.prepTime.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="prepTime">
                                                {mergeOptions?.prepTime +
                                                    " " +
                                                    t("units.minute.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.prepTimeBufferPercent.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="prepTimeBufferPercent">
                                                {"%" + mergeOptions?.prepTimeBufferPercent}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.handOverTime.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="handOverTime">
                                                {mergeOptions?.handOverTime +
                                                    " " +
                                                    t("units.minute.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.mergeMaxOrder.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="mergeMaxOrder">
                                                {mergeOptions?.maxOrder +
                                                    " " +
                                                    t("units.piece.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.mergeWithOtherVehicleTypes.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="mergeWithOtherVehicleTypes">
                                                {mergeOptions?.mergeWithOtherTypes
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.mergeWithOtherCompanies.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="mergeWithOtherCompanies">
                                                {mergeOptions?.mergeWithOtherCompanies
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.maxPickup.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="maxPickup">
                                                {mergeOptions?.maxPickup +
                                                    " " +
                                                    t("units.piece.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.regionRestriction.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="regionRestriction">
                                                {mergeOptions?.regionRestriction
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                    </OrderMergeContainer>
                                ) : null
                            )}
                        {stores.meStore.role == "admin" &&
                            companyData.data?.options?.queue?.isActive && (
                                <OrderMergeContainer size="small" bordered>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.orderQueue.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="orderQueueable">
                                            {companyData.data?.options?.queue?.isActive
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.maxOrderCount.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="maxOrderCount">
                                            {companyData.data?.options?.queue?.maxCount}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.maxWaitingTime.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="maxWaitingTime">
                                            {companyData.data?.options?.queue?.maxWaitingTime +
                                                " " +
                                                t("units.minute.long", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.thresholdCount.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="thresholdCount">
                                            {"%" + companyData.data?.options?.queue?.thresholdCount}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.thresholdTime.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="thresholdTime">
                                            {companyData.data?.options?.queue?.thresholdTime +
                                                " " +
                                                t("units.minute.long", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                </OrderMergeContainer>
                            )}
                    </Col>

                    <Col span={24} data-testid="subCompanyCard">
                        <Row justify={"space-between"}>
                            <Row justify={"start"}>
                                <Space size={20}>
                                    <Select
                                        showSearch
                                        value={activity}
                                        data-testid="subCompanyActivitySelect"
                                        onChange={value => {
                                            setCurrentPage(1);
                                            setActivity(value);
                                        }}
                                    >
                                        <Select.Option
                                            value="all"
                                            data-testid="subCompanyActivitySelect_option"
                                        >
                                            {t("companyDetail.allSubcompanies", {
                                                ns: "companyDetail"
                                            })}
                                        </Select.Option>
                                        <Select.Option
                                            value="enabled"
                                            data-testid="subCompanyActivitySelect_option"
                                        >
                                            {t("companyDetail.activeSubcompanies", {
                                                ns: "companyDetail"
                                            })}
                                        </Select.Option>
                                        <Select.Option
                                            value="disabled"
                                            data-testid="subCompanyActivitySelect_option"
                                        >
                                            {t("companyDetail.inactiveSubcompanies", {
                                                ns: "companyDetail"
                                            })}
                                        </Select.Option>
                                    </Select>

                                    <CityContainer>
                                        <CityFilter
                                            showLabel={false}
                                            value={searchCityText}
                                            onChange={value => {
                                                setCurrentPage(1);
                                                setSearchCityText(value);
                                            }}
                                        />
                                    </CityContainer>
                                    <SearchInput
                                        placeholder={t("filters.search", {ns: "common"})}
                                        value={text}
                                        data-testid="searchInput"
                                        onSearch={value => {
                                            setCurrentPage(1);
                                            setSearchText(value);
                                        }}
                                        onChange={event => {
                                            setText(event.target.value);
                                        }}
                                        enterButton={
                                            <CustomButton
                                                data-testid="searchInput_searchButton"
                                                icon={<SearchOutlined />}
                                            />
                                        }
                                        suffix={
                                            text ? (
                                                <CloseIcon
                                                    data-testid="searchInput_deleteButton"
                                                    onClick={() => {
                                                        setCurrentPage(1);
                                                        setSearchText("");
                                                        setText("");
                                                    }}
                                                />
                                            ) : (
                                                <span />
                                            )
                                        }
                                    />
                                </Space>
                            </Row>

                            <Row justify="end">
                                {stores.meStore.checkRole("addSubCompany") && (
                                    <CustomButton
                                        type="primary"
                                        data-testid="addSubCompanyButton"
                                        onClick={() => {
                                            history.push("/companies/add-sub-company/" + companyId);
                                        }}
                                        icon={<PlusOutlined />}
                                    >
                                        {t("companyDetail.actions.addSubcompany", {
                                            ns: "companyDetail"
                                        })}
                                    </CustomButton>
                                )}

                                <CustomButton
                                    type="primary"
                                    data-testid="exportWithExcelButton"
                                    onClick={async () => {
                                        const response = await stores.companyStore.downloadSubCompanyExcelFile(
                                            {
                                                companyId
                                            }
                                        );
                                        let link = document.createElement("a");
                                        link.href = response;
                                        link.download = `${companyData.data.companyName} alt firma listesi.xlsx`;
                                        document.body.appendChild(link);
                                        link.click();
                                        setTimeout(function() {
                                            window.URL.revokeObjectURL(link);
                                        }, 200);
                                    }}
                                    icon={<DownloadOutlined />}
                                >
                                    {t("companyDetail.actions.exportWithExcel", {
                                        ns: "companyDetail"
                                    })}
                                </CustomButton>

                                {stores.meStore.checkRole("importSubcompaniesFromExcel") && (
                                    <Upload {...fileUploadProps} accept=".xlsx,.xls,.csv,.tsv">
                                        <CustomButton
                                            icon={<UploadOutlined />}
                                            data-testid="importFromExcelButton"
                                        >
                                            {t("companyDetail.actions.importFromExcel", {
                                                ns: "companyDetail"
                                            })}
                                        </CustomButton>
                                    </Upload>
                                )}
                                {stores.meStore.checkRole("downloadSubcompaniesExcelTemplate") && (
                                    <CustomButton
                                        href={`${process.env.PUBLIC_URL}/import-templates/subcompany-import-template-v2.xlsx`}
                                        type="ghost"
                                        icon={<DownloadOutlined />}
                                        data-testid="downloadExcelTemplete"
                                    >
                                        {t("companyDetail.actions.downloadExcelTemplate", {
                                            ns: "companyDetail"
                                        })}
                                    </CustomButton>
                                )}
                            </Row>
                        </Row>
                        <Table
                            rowKey="_id"
                            data-testid="subCompanyTable"
                            dataSource={subCompanyData && subCompanyData.rows}
                            loading={subCompanyLoading}
                            style={{marginTop: 20}}
                            pagination={{
                                total: subCompanyData.dataCount,
                                current: currentPage,
                                onChange: async page => {
                                    setCurrentPage(page);
                                },
                                hideOnSinglePage: true,
                                showSizeChanger: false
                            }}
                            locale={{
                                emptyText: <EmptyMessage />
                            }}
                            bordered
                            expandable={{
                                expandedRowRender: record => {
                                    const center = {
                                        lat: record.location.latitude,
                                        lng: record.location.longitude
                                    };
                                    return (
                                        <SingleMarkerMap center={center} icon={<ShopOutlined />} />
                                    );
                                }
                            }}
                        >
                            <Column
                                title={t("fields.companyName", {ns: "common"})}
                                dataIndex="name"
                                render={(text, index) => (
                                    <Link
                                        data-testid="subCompanyName"
                                        target="_blank"
                                        to={{
                                            pathname: "/companies/sub-company/" + index._id
                                        }}
                                    >
                                        {text}
                                    </Link>
                                )}
                            />
                            <Column
                                title={t("fields.city", {ns: "common"})}
                                dataIndex="city"
                                render={text => (
                                    <span data-testid="city">{text?.capitalize()}</span>
                                )}
                            />
                            <Column
                                title={t("fields.county", {ns: "common"})}
                                dataIndex="county"
                                render={text => (
                                    <span data-testid="county">{text?.capitalize()}</span>
                                )}
                            />
                            <Column
                                title={t("companyDetail.fields.district", {
                                    ns: "companyDetail"
                                })}
                                dataIndex="district"
                                render={text => (
                                    <span data-testid="district">{text?.capitalize()}</span>
                                )}
                            />
                            <Column
                                title={t("fields.address", {ns: "common"})}
                                dataIndex="address"
                                render={text => <span data-testid="address">{text}</span>}
                            />
                            {stores.meStore.checkRole("changeSubcompanyOnboardingStatus") && (
                                <Column
                                    title={
                                        <>
                                            Alt Firma <br />
                                            Onay Durumu
                                        </>
                                    }
                                    dataIndex="onboardingStatus"
                                    render={(text, row) => (
                                        <span data-testid="onboardingStatus">
                                            <ChangeSubcompanyOnboardingStatusInput
                                                subCompany={row}
                                                recall={() =>
                                                    setSubCompanyRecall(!subCompanyRecall)
                                                }
                                            />
                                        </span>
                                    )}
                                />
                            )}
                            {stores.meStore.checkRole("updateCompany") && (
                                <Column
                                    title={t("actions.action", {ns: "common"})}
                                    dataIndex="_id"
                                    width={200}
                                    render={(text, row) => (
                                        <Row>
                                            <ConditionalTooltip
                                                text={t(
                                                    "companyDetail.updateSubcompanyStatusTooltip",
                                                    {ns: "companyDetail"}
                                                )}
                                                child={
                                                    <CustomButton
                                                        danger={!row.disabled}
                                                        type="primary"
                                                        data-testid="updateSubCompanyStatusButton"
                                                        onClick={() =>
                                                            //handleSubCompanyUpdateStatus(row)
                                                            selectedSubCompanyForUpdateStatus(row)
                                                        }
                                                        icon={
                                                            row.disabled ? (
                                                                <CheckOutlined />
                                                            ) : (
                                                                <CloseOutlined />
                                                            )
                                                        }
                                                    >
                                                        {row.disabled
                                                            ? t("actions.activate", {
                                                                  ns: "common"
                                                              })
                                                            : t("actions.inactivate", {
                                                                  ns: "common"
                                                              })}
                                                    </CustomButton>
                                                }
                                            />
                                            {!row.disabled && (
                                                <Link
                                                    target="_blank"
                                                    to={`/companies/update-sub-company/${row._id}`}
                                                >
                                                    <CustomButton
                                                        data-testid="updateSubCompanyButton"
                                                        icon={<EditOutlined />}
                                                    >
                                                        {t("actions.update", {ns: "common"})}
                                                    </CustomButton>
                                                </Link>
                                            )}
                                            <CustomButton
                                                data-testid="subCompanyPricingButton"
                                                onClick={() =>
                                                    history.push(
                                                        `/sub-company/payment-contract/${row._id}`
                                                    )
                                                }
                                            >
                                                Fiyatlandırma
                                            </CustomButton>
                                        </Row>
                                    )}
                                />
                            )}
                        </Table>
                    </Col>
                </Row>
                <ChangeSubcompanyStatusModal
                    visibility={subCompanyUpdateStatusModalVisibility}
                    onClose={() => {
                        setSubCompanyUpdateStatusModalVisibility(false);
                        setSelectedSubCompany(undefined);
                    }}
                    subCompany={selectedSubCompany}
                    recall={() => setSubCompanyRecall(!subCompanyRecall)}
                />
            </>
        )
    );
};
